/**
 * Ad attribution: the `gclid`, `fbclid` and `utm_*` parameters a visitor
 * lands with, carried through to Shopify.
 *
 * They arrive on whatever page an ad or a campaign links to, which is seldom
 * the checkout, so they are saved on whichever page the visitor allows it and
 * the Checkout hands them to Shopify twice: on the checkout URL, where
 * Shopify's attribution and the shop's ad pixels read them, and as cart
 * attributes, which keep them on the order itself.
 *
 *  - only with the visitor's consent. A click id is what the ad tags' own
 *    cookies would hold, so it follows the same rule as those tags: the page's
 *    Termly script calls `Codeverse.setConsent` on each page and whenever the
 *    visitor changes their choices (`applyConsent`). Advertising granted saves
 *    the page's parameters; anything else deletes what is saved. Without that
 *    call, as when a blocker stops Termly, nothing is saved.
 *  - localStorage, not sessionStorage like the checkout draft: a parent who
 *    clicks an ad today and buys next week was still brought by that ad. They
 *    are kept for `ATTRIBUTION_TTL_MS`.
 *  - the newest visit wins: a visit carrying any of them replaces the whole
 *    saved set, so one click's `gclid` never travels with another visit's
 *    `utm_source`. A visit carrying none, such as a page within the site,
 *    leaves the set alone.
 *  - a purchase does not clear them, so a family who buys again is credited
 *    the same way until a newer visit says otherwise.
 */

/** Versioned: change the suffix when the shape changes and old sets are dropped. */
export const ATTRIBUTION_KEY = 'cv.attribution.v1'

export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Bounds on what is carried, so a mangled or hostile link can never make
 * Shopify refuse the cart: a real click id or campaign tag is far inside them.
 */
const MAX_PARAMS = 20
const MAX_KEY_LENGTH = 64
const MAX_VALUE_LENGTH = 500

/** One parameter, in the shape Shopify takes a cart attribute. */
export interface AttributionParam {
  key: string
  value: string
}

function isCarried(key: string): boolean {
  return (
    (key === 'gclid' || key === 'fbclid' || key.startsWith('utm_')) && key.length <= MAX_KEY_LENGTH
  )
}

function isUsable({ key, value }: AttributionParam): boolean {
  return isCarried(key) && value !== '' && value.length <= MAX_VALUE_LENGTH
}

/** The parameters in a query string worth carrying, in the order they came. */
export function readAttribution(search: string): AttributionParam[] {
  const params: AttributionParam[] = []
  const seen = new Set<string>()
  for (const [key, raw] of new URLSearchParams(search)) {
    // A repeated parameter counts once, as its first value, like `get()`.
    if (seen.has(key)) continue
    seen.add(key)
    const param = { key, value: raw.trim() }
    if (isUsable(param)) params.push(param)
  }
  return params.slice(0, MAX_PARAMS)
}

/** Save the landing page's parameters, if it has any, in place of the last visit's. */
export function captureAttribution(search: string, now = Date.now()): void {
  const params = readAttribution(search)
  if (params.length === 0) return
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ savedAt: now, params }))
  } catch {
    // Blocked storage loses the attribution, never the page.
  }
}

/** Forget the saved parameters. */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_KEY)
  } catch {
    // Storage that can't be read can't have saved anything either.
  }
}

/** What the page's consent manager says the visitor allows. */
export interface Consent {
  /** Termly's Advertising category. */
  advertising?: boolean
  /** Termly's Analytics category. */
  analytics?: boolean
}

/**
 * The latest answer the page reported, or none before it has. Kept in memory
 * only: the page's Termly script reports on every page, after the bundle
 * loads, so the checkout page has it before Continue can be pressed. Before
 * the first report, nothing is known and Shopify is told nothing.
 */
let consent: Required<Consent> | undefined

/**
 * Act on the visitor's consent, as `Codeverse.setConsent` passes it on: keep
 * it for the checkout (`currentConsent`), save this page's parameters when
 * Advertising is granted, and otherwise delete what is saved. The page's code
 * is plain JavaScript, so only `true` grants, in every category.
 */
export function applyConsent(
  reported: Consent | undefined,
  search = window.location.search,
  now = Date.now(),
): void {
  consent = {
    advertising: reported?.advertising === true,
    analytics: reported?.analytics === true,
  }
  if (consent.advertising) captureAttribution(search, now)
  else clearAttribution()
}

/** The consent as last reported, every category answered, or none before the first report. */
export function currentConsent(): Required<Consent> | undefined {
  return consent
}

/** Forget the reported consent, as a fresh page would have. Tests only. */
export function resetConsent(): void {
  consent = undefined
}

/**
 * The saved parameters, or none once they have expired. Like the draft, the
 * stored value is outside our control, so anything malformed reads as none.
 */
export function loadAttribution(now = Date.now()): AttributionParam[] {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY)
    if (!raw) return []
    const saved = JSON.parse(raw) as { savedAt?: unknown; params?: unknown }
    if (typeof saved?.savedAt !== 'number' || now - saved.savedAt > ATTRIBUTION_TTL_MS) return []
    if (!Array.isArray(saved.params)) return []
    return saved.params
      .filter(
        (param): param is AttributionParam =>
          typeof param?.key === 'string' && typeof param?.value === 'string' && isUsable(param),
      )
      .map(({ key, value }) => ({ key, value }))
      .slice(0, MAX_PARAMS)
  } catch {
    return []
  }
}

/**
 * The checkout URL with the parameters on it. Shopify's own query, the cart's
 * key, is kept; a parameter of ours replaces one of the same name.
 */
export function withAttribution(checkoutUrl: string, params: readonly AttributionParam[]): string {
  if (params.length === 0) return checkoutUrl
  try {
    const url = new URL(checkoutUrl)
    for (const { key, value } of params) url.searchParams.set(key, value)
    return url.toString()
  } catch {
    // Unreachable with Shopify's absolute URL; the checkout matters more.
    return checkoutUrl
  }
}
